import { Link } from 'react-router';
import { buildMeta, breadcrumbSchema, localBusinessSchema, faqSchema } from '~/lib/seo';
import { SITE } from '~/data/site';
import { CITIES } from '~/data/cities';
import { SALES_TAX } from '@shared/pricing';

const AREA_FAQ = [
  {
    q: 'What areas of Tampa Bay do you cover?',
    a: 'Tampa, Brandon, Riverview, St. Petersburg, Clearwater, Lakeland and Wesley Chapel, plus the surrounding parts of Hillsborough, Pinellas, Pasco and Polk counties. Each of those cities has its own page with the specific corridors we work.',
  },
  {
    q: 'Do you charge more for buildings further from Brandon?',
    a: 'No. The published per-square-foot rates are the same everywhere we say we work. If a building is far enough out that travel genuinely changes the number, we tell you that before quoting rather than burying it in the rate.',
  },
  {
    q: 'Why does sales tax differ between counties?',
    a: 'Florida charges 6% state sales tax on commercial cleaning plus a county discretionary surtax, so the total is 7.5% in Hillsborough and 7% in Pinellas, Pasco and Polk. Residential cleaning is exempt; commercial is not. It appears as its own line on the invoice.',
  },
  {
    q: 'We are just outside the cities you list. Will you still come?',
    a: 'Usually yes — ask us. We would rather tell you straight than have you assume the answer is no, and we would rather say no than take a building we cannot service properly.',
  },
] as const;

export function meta() {
  return buildMeta({
    title: 'Service area across Tampa Bay',
    description: `Commercial cleaning in ${SITE.serviceCities.slice(0, 5).join(', ')} and the surrounding Tampa Bay area. Based in Brandon, working across Hillsborough, Pinellas, Pasco and Polk.`,
    path: '/service-area',
    jsonLd: [
      breadcrumbSchema([
        { name: 'Home', url: '/' },
        { name: 'Service area', url: '/service-area' },
      ]),
      localBusinessSchema(),
      faqSchema(AREA_FAQ),
    ],
  });
}

export default function ServiceArea() {
  return (
    <>
      <section className="hero dusk grain">
        <div className="shell">
          <div className="hero-inner">
            <p className="eyebrow-pill">Service area</p>
            <h1>Based in Brandon. Working across the bay.</h1>
            <p className="lede">
              We are a Tampa Bay company, not a national franchise with a local phone number. If
              your building is on this list, we can be there tonight.
            </p>
          </div>
        </div>
      </section>

      <section className="section">
        <div className="shell">
          <div className="split">
            <div className="stack">
              <h2>Cities we cover</h2>
              {/* These are LINKS, not chips. A city page that nothing links to does not
                  get crawled, and an uncrawled page cannot rank — which would defeat the
                  entire reason for building them. */}
              <ul className="dash">
                {CITIES.map((c) => (
                  <li key={c.slug}>
                    <Link to={`/commercial-cleaning/${c.slug}`}>
                      Commercial cleaning in {c.name}
                    </Link>{' '}
                    <span className="small">
                      — {c.county} County, {c.driveFromBase}
                    </span>
                  </li>
                ))}
              </ul>
              <p>
                Plus the surrounding areas in Hillsborough, Pinellas, Pasco and Polk counties. If
                you are near the edge of that, ask — the answer is usually yes.
              </p>
            </div>

            <div className="stack split-sticky">
              <div className="callout">
                <p className="eyebrow">A note on sales tax</p>
                <p>
                  Commercial cleaning is a taxable service in Florida, and the rate depends on which
                  county your building is in. Residential cleaning is exempt; commercial is not.
                </p>
                <ul className="dash">
                  {SALES_TAX.counties.map((c) => (
                    <li key={c.county}>
                      {c.county} — {(c.rate * 100).toFixed(1)}%
                    </li>
                  ))}
                </ul>
                <p className="small">
                  6% state plus the county surtax. Rates as of {SALES_TAX.asOf}. It appears as its
                  own line on your invoice.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="night-band">
        <div className="shell">
          <div className="stack" style={{ maxWidth: '46ch' }}>
            <p className="eyebrow">Why local matters here</p>
            <h2>A franchise office is not the same as a local crew.</h2>
            <p>
              Most of the big names in Tampa commercial cleaning are national franchises. The person
              who sells you the contract is not the person who staffs it, and when it goes wrong the
              escalation path runs out of state.
            </p>
            <p>
              We are one company across this bay. The person who walks your building is the person
              who owns whether it goes right.
            </p>
          </div>
        </div>
      </section>

      <section className="section-tight">
        <div className="shell">
          <div className="faq">
            <h2>Questions about coverage</h2>
            {AREA_FAQ.map((item) => (
              <details key={item.q}>
                <summary>{item.q}</summary>
                <p>{item.a}</p>
              </details>
            ))}
          </div>
        </div>
      </section>

      <section className="dawn">
        <div className="shell">
          <div className="cta-inner">
            <h2>Is your building in range?</h2>
            <p>Tell us where it is and we will tell you straight.</p>
            <div className="btn-row">
              <Link to="/quote" className="btn btn-primary">
                Get a quote
              </Link>
            </div>
          </div>
        </div>
      </section>
    </>
  );
}
